<template>
  <div class="app-container">
    <Header />

    <div class="blog-content-container">
      <div :class="mainContentClass">
        <div class="blog-header">
          <span class="blog-date">{{ date }}</span>
          <h1 class="blog-title">{{ title }}</h1>
          <div class="blog-meta">
            <div class="author-info">
              <img
                :src="authorAvatar || '/blog_assets/logo.png'"
                class="author-avatar"
              />
              <span class="author-name">{{ author || "Skyhost Team" }}</span>
            </div>
          </div>
        </div>

        <div class="education-blog">
          <section class="blog-intro">
            <p class="intro-text">
              Bring education to life with live drone tours, where students and
              teachers can explore remote wonders in real time, right from their
              classroom. These immersive journeys offer a unique perspective
              that traditional field trips simply can't match.
            </p>
          </section>

          <section class="blog-section learning-section">
            <div class="section-content">
              <h2>Making Learning Stick</h2>
              <p>
                Studies show that regular field trips improve academic
                performance and foster greater tolerance for different opinions.
                Live drone tours create memorable learning experiences by
                connecting classroom lessons with real-world visuals.
              </p>
              <p>
                With a drone tour, students experience real-time views of
                incredible locations, from towering peaks to hidden forests.
                They can ask questions directly to expert drone pilots during
                the session and even help control the journey by suggesting
                where the drone should fly next. This interactive approach
                transforms passive learning into active discovery.
              </p>
              <div class="highlight-box">
                <p>
                  "The interactivity of live drone tours creates neural pathways
                  that enhance retention and foster a deeper understanding of
                  complex subjects."
                </p>
              </div>
            </div>
          </section>

          <section class="blog-section access-section">
            <div class="section-content">
              <h2>Breaking Down Barriers to Educational Access</h2>
              <p>
                Traditional field trips are often expensive and difficult to
                organize, with issues like high travel costs, limited staff, and
                complex planning. Live drone tours remove these barriers,
                offering an easy, affordable, and enriching alternative.
              </p>
              <p>
                Research shows that students' location, school type, and
                socio-economic status affect field trip attendance.
                Unfortunately, this limits access for students in underfunded or
                rural districts. Remote Outdoor Classes help bridge this gap by
                eliminating transportation costs, requiring minimal setup,
                making exotic locations accessible to everyone, and supporting
                curriculum goals with targeted content.
              </p>
              <div class="quote-container">
                <blockquote>
                  "The best way to learn about the world is to see it with your
                  own eyes."
                </blockquote>
              </div>
            </div>
          </section>

          <section class="blog-section benefits-section">
            <div class="section-content">
              <h2>The Educational Power of Live Drone Tours</h2>
              <p>
                Remote outdoor classes excel by creating multi-sensory learning
                experiences that engage students on multiple levels
                simultaneously. This immersive approach transforms abstract
                concepts into tangible realities.
              </p>
              <div class="benefits-cards">
                <div class="benefit-card">
                  <div class="benefit-icon">🎨</div>
                  <div class="card-header">
                    <h3>Immersive Experiences</h3>
                  </div>
                  <p>
                    Real-time views from breathtaking locations create visceral
                    learning experiences. Students don't just learn about a
                    volcano—they see its steaming crater and hear the guide
                    describe the geological processes in real time.
                  </p>
                </div>
                <div class="benefit-card">
                  <div class="benefit-icon">📚</div>
                  <div class="card-header">
                    <h3>Curriculum Integration</h3>
                  </div>
                  <p>
                    Content ties directly to curriculum topics, reinforcing
                    classroom learning. When studying ecosystems, students can
                    observe actual wildlife interactions rather than just
                    reading about them in textbooks.
                  </p>
                </div>
                <div class="benefit-card">
                  <div class="benefit-icon">🌍</div>
                  <div class="card-header">
                    <h3>Global Perspective</h3>
                  </div>
                  <p>
                    Students explore stunning landscapes and cultural sites from
                    around the world, developing a more nuanced understanding of
                    global interconnections and diverse perspectives through
                    first-hand observation.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section class="blog-section sdg-section">
            <div class="section-content">
              <h2>Supporting Sustainable Development Goals</h2>
              <p>
                These immersive journeys connect students to locations and
                topics under the UN's Sustainable Development Goals, helping
                them understand complex global challenges through direct
                observation. Virtual explorations bring to life concepts like
                climate action, marine ecosystem conservation, sustainable
                cities, and cultural heritage preservation.
              </p>
              <p>
                By witnessing these environments firsthand, students develop
                deeper connections to global sustainability challenges. A drone
                flight over a coral reef affected by warming oceans creates a
                profound emotional impact that statistics alone cannot achieve.
                Similarly, exploring sustainable urban developments helps
                students envision future possibilities for their own
                communities.
              </p>
            </div>
          </section>

          <section class="blog-section process-section">
            <div class="section-content">
              <h2>The Virtual Field Trip Experience</h2>
              <p>
                The virtual field trip process is elegantly simple. Educators
                select from curated destinations that align with their
                curriculum goals—anything from wildlife habitats to volcanic
                landscapes to historical landmarks. Setup requires only a
                projector, internet connection, and curious minds.
              </p>
              <p>
                During the experience, students watch the live drone feed and
                engage directly with the pilot-guide, asking questions and
                suggesting exploration directions. This creates a collaborative
                atmosphere where students become active participants rather than
                passive observers. The entire experience is designed to be
                low-stress for teachers and high-engagement for students.
              </p>
            </div>
          </section>

          <section class="blog-section" style="border-bottom: none">
            <div class="testimonial">
              <p>
                "This was the easiest and most engaging field trip we've ever
                done. No travel, no stress—just pure excitement and immersive
                learning."
              </p>
              <p class="author">— James H., 5th Grade Teacher</p>
            </div>
            <div class="closing">
              <h3>Transform Your Classroom Experience</h3>
              <p>
                Don't let budget constraints and logistical challenges limit
                your students' horizons. Give your classroom the gift of
                exploration with live drone tours that make learning vivid,
                accessible, and unforgettable.
              </p>
            </div>
          </section>
        </div>

        <div class="blog-footer">
          <div class="blog-tags" v-if="tags && tags.length">
            <span class="tag-label">Tags:</span>
            <span v-for="(tag, index) in tags" :key="index" class="blog-tag">
              {{ tag }}
            </span>
          </div>

          <div class="share-section">
            <span class="share-label">Share this article:</span>
            <div class="share-icons">
              <vue-feather
                type="twitter"
                size="18"
                class="share-icon"
                @click="shareOnTwitter"
              ></vue-feather>
              <vue-feather
                type="facebook"
                size="18"
                class="share-icon"
                @click="shareOnFacebook"
              ></vue-feather>
              <vue-feather
                type="linkedin"
                size="18"
                class="share-icon"
                @click="shareOnLinkedIn"
              ></vue-feather>
              <vue-feather
                type="mail"
                size="18"
                class="share-icon"
                @click="shareByEmail"
              ></vue-feather>
            </div>
          </div>

          <div class="related-posts" v-if="relatedBlogs.length">
            <h3 class="related-title">Related articles</h3>
            <div class="related-grid">
              <div
                v-for="relatedBlog in relatedBlogs"
                :key="relatedBlog.id"
                class="related-card"
                @click="navigateToBlog(relatedBlog.id)"
              >
                <img
                  :src="relatedBlog.image"
                  alt="Related blog image"
                  class="related-image"
                />
                <div class="related-content">
                  <h4 class="related-blog-title">{{ relatedBlog.title }}</h4>
                  <span class="related-date">{{ relatedBlog.date }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="back-to-blogs">
            <button class="blog-btn" @click="navigateToBlogs">
              <vue-feather
                type="arrow-left"
                size="16"
                class="back-icon"
              ></vue-feather>
              Back to all blogs
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "EducationBlog",
  components: {
    Header,
    Footer,
  },
  data() {
    const blogId = "education-drones";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar,
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    shareOnTwitter() {
      const text = `${this.title} - Check out this article by Skyhost`;
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(this.currentPageUrl)}`;
      window.open(url, "_blank");
    },
    shareOnFacebook() {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareByEmail() {
      const subject = encodeURIComponent(this.title);
      const body = encodeURIComponent(
        `I thought you might find this article interesting: ${this.title}\n\n${this.currentPageUrl}`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    this.relatedBlogs = Object.values(blogsRegistry)
      .filter((blog) => blog.id !== this.id)
      .slice(0, 2); // Limit to 2 related blogs
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });

    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}

.blog-header {
  margin-bottom: 2rem;
}

.blog-date {
  color: #666;
  font-size: 1rem;
}

.blog-title {
  font-size: 2.5rem;
  margin: 1rem 0;
  color: #000;
  line-height: 1.3;
}

.blog-meta {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 20px;
  height: 20px;
  /* border-radius: 50%; */
  margin-right: 0.8rem;
  object-fit: contain;
}

.author-name {
  font-weight: 500;
  color: #333;
}

.education-blog {
  line-height: 1.8;
  color: #333;
  font-family: "Inter", "Helvetica", sans-serif;
}

.blog-intro {
  margin-bottom: 4rem;
}

.intro-text {
  font-size: 1.25rem;
  line-height: 2;
  color: #222;
}

.blog-section {
  margin-bottom: 4rem;
  padding: 2rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.section-content {
  max-width: 100%;
}

h2 {
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 600;
}

h3 {
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 500;
}

p {
  margin-bottom: 1.5rem;
  font-size: 1.05rem;
}

.highlight-box {
  background-color: #f8f9fa;
  border-left: 4px solid #000;
  padding: 1.5rem;
  margin: 2rem 0;
  font-size: 1.1rem;
}

.quote-container {
  text-align: center;
  margin: 3rem 0;
}

blockquote {
  font-size: 1.5rem;
  line-height: 1.6;
  font-style: italic;
  color: #333;
  position: relative;
  max-width: 80%;
  margin: 0 auto;
}

blockquote:before,
blockquote:after {
  content: '"';
  font-size: 3rem;
  color: #ddd;
  position: absolute;
}

blockquote:before {
  left: -2rem;
  top: -1rem;
}

blockquote:after {
  right: -2rem;
  bottom: -2rem;
}

.benefits-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 2.5rem 0;
}

.benefit-card {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  height: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.benefit-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.benefit-icon {
  font-size: 2rem;
  margin-right: 1rem;
}

.testimonial {
  background-color: #f8f9fa;
  padding: 2rem;
  border-radius: 8px;
  margin-bottom: 2.5rem;
  text-align: center;
}

.testimonial p {
  font-size: 1.3rem;
  font-style: italic;
  margin-bottom: 0.5rem;
}

.author {
  font-weight: 500;
  font-style: normal;
  color: #666;
}

.closing {
  text-align: left;
  max-width: 100%;
  margin: 0 auto;
}

/* .closing h3 {
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
}

.closing p {
  font-size: 1.2rem;
} */

.blog-footer {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
}

.blog-tags {
  margin-bottom: 2rem;
}

.tag-label {
  font-weight: 500;
  margin-right: 0.5rem;
}

.blog-tag {
  display: inline-block;
  padding: 0.3rem 0.8rem;
  margin: 0.3rem;
  background-color: #f0f0f0;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #666;
}

.share-section {
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
}

.share-label {
  font-weight: 500;
  margin-right: 1rem;
}

.share-icons {
  display: flex;
  gap: 1rem;
}

.share-icon {
  cursor: pointer;
  color: #555;
}

.share-icon:hover {
  color: #1565c0;
}

.related-posts {
  margin-bottom: 3rem;
}

.related-title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #000;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.related-card {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: transform 0.3s ease;
}

.related-card:hover {
  transform: translateY(-3px);
}

.related-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.related-content {
  padding: 1rem;
}

.related-blog-title {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #000;
}

.related-date {
  font-size: 0.8rem;
  color: #666;
}

.back-to-blogs {
  margin-top: 3rem;
  text-align: center;
}

.blog-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  background-color: #f0f0f0;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.blog-btn:hover {
  background-color: #e0e0e0;
}

.back-icon {
  margin-right: 0.5rem;
}

@media (max-width: 1024px) {
  .benefits-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .benefits-cards {
    grid-template-columns: 1fr;
  }

  blockquote {
    max-width: 100%;
  }

  blockquote:before,
  blockquote:after {
    display: none;
  }

  .closing {
    max-width: 100%;
  }

  .blog-title {
    font-size: 2rem;
  }

  .related-grid {
    grid-template-columns: 1fr;
  }
}
</style>
